import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productsAPI, categoriesAPI, settingsAPI } from '../../utils/api';
import { localized } from '../../utils/localized';
import ProductCard from '../../components/shop/ProductCard';

const HERO_DEFAULTS = {
  hero_eyebrow: 'Pure. Natural. Authentic.',
  hero_title_line1: "Nature's Golden",
  hero_title_line2: 'Gift to Kuwait',
  hero_subtitle: "Premium honey products sourced from the world's finest apiaries — delivered to your door across Kuwait.",
  hero_cta_primary: 'Shop Now',
  hero_cta_secondary: 'Our Story',
};

const HomePage = () => {
  const [featured, setFeatured] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hero, setHero] = useState(HERO_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  useEffect(() => {
    Promise.all([
      productsAPI.getAll({ featured: true, limit: 4 }),
      productsAPI.getAll({ bestseller: true, limit: 4 }),
      categoriesAPI.getAll(),
      settingsAPI.getPublic().catch(() => []),
    ]).then(([feat, best, cats, settings]) => {
      setFeatured(feat.products || []);
      setBestsellers(best.products || []);
      const HIDDEN = new Set(['gift-sets']);
      setCategories((cats || []).filter(c => !HIDDEN.has(c.slug)));
      const map = Object.fromEntries((settings || []).map(s => [s.setting_key, s.setting_value]));
      setHero({
        hero_eyebrow: map.hero_eyebrow || HERO_DEFAULTS.hero_eyebrow,
        hero_eyebrow_ar: map.hero_eyebrow_ar,
        hero_title_line1: map.hero_title_line1 || HERO_DEFAULTS.hero_title_line1,
        hero_title_line1_ar: map.hero_title_line1_ar,
        hero_title_line2: map.hero_title_line2 || HERO_DEFAULTS.hero_title_line2,
        hero_title_line2_ar: map.hero_title_line2_ar,
        hero_subtitle: map.hero_subtitle || HERO_DEFAULTS.hero_subtitle,
        hero_subtitle_ar: map.hero_subtitle_ar,
        hero_cta_primary: map.hero_cta_primary || HERO_DEFAULTS.hero_cta_primary,
        hero_cta_primary_ar: map.hero_cta_primary_ar,
        hero_cta_secondary: map.hero_cta_secondary || HERO_DEFAULTS.hero_cta_secondary,
        hero_cta_secondary_ar: map.hero_cta_secondary_ar,
      });
    }).finally(() => setLoading(false));
  }, []);

  // Helper that prefers an _ar variant when language is ar
  const heroVal = (key) => (lang === 'ar' && hero[`${key}_ar`]) ? hero[`${key}_ar`] : hero[key];

  return (
    <div className="homepage">
      {/* HERO */}
      <section className="hero">
        <div className="hero__bg" />
        <div className="container hero__content">
          <span className="hero__eyebrow">{heroVal('hero_eyebrow')}</span>
          <h1 className="hero__title">{heroVal('hero_title_line1')}<br /><em>{heroVal('hero_title_line2')}</em></h1>
          <p className="hero__sub">{heroVal('hero_subtitle')}</p>
          <div className="hero__actions">
            <Link to="/shop" className="btn btn-primary hero__cta">{heroVal('hero_cta_primary')}</Link>
            <Link to="/about" className="btn btn-outline hero__cta-secondary">{heroVal('hero_cta_secondary')}</Link>
          </div>
          <div className="hero__trust">
            <span>✅ {t('home.trust.natural')}</span>
            <span>✅ {t('home.trust.delivery')}</span>
            <span>✅ {t('home.trust.labTested')}</span>
            <span>✅ {t('home.trust.securePayment')}</span>
          </div>
        </div>
        <div className="hero__honeycomb" aria-hidden="true">
          {[...Array(12)].map((_, i) => <div key={i} className="honeycomb-cell" />)}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="section categories-section">
        <div className="container">
          <div className="section-header">
            <h2>{t('home.exploreOurRange')}</h2>
            {t('home.exploreSubtitle') && <p>{t('home.exploreSubtitle')}</p>}
          </div>
          <div className="categories-grid">
            {categories.slice(0, 4).map(cat => (
              <Link to={`/shop?category=${cat.slug}`} key={cat.id} className="category-card">
                <div className="category-card__icon">{getCatIcon(cat.slug)}</div>
                <h3>{localized(cat, 'name', lang)}</h3>
                <span>{cat.product_count} {t('home.products')}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="section products-section">
          <div className="container">
            <div className="section-header">
              <h2>{t('home.featuredCollection')}</h2>
              <Link to="/shop" className="section-header__link">{t('home.viewAll')} →</Link>
            </div>
            <div className="products-grid">
              {featured.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* WHY US */}
      <section className="section why-section">
        <div className="container">
          <div className="section-header">
            <h2>{t('home.whyUs')}</h2>
          </div>
          <div className="why-grid">
            {[
              { icon: '🔬', title: t('home.why.labTitle'), desc: t('home.why.labDesc') },
              { icon: '🌿', title: t('home.why.naturalTitle'), desc: t('home.why.naturalDesc') },
              { icon: '🚚', title: t('home.why.deliveryTitle'), desc: t('home.why.deliveryDesc') },
              { icon: '💳', title: t('home.why.secureTitle'), desc: t('home.why.secureDesc') },
            ].map(item => (
              <div key={item.title} className="why-card">
                <span className="why-card__icon">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BESTSELLERS */}
      {bestsellers.length > 0 && (
        <section className="section products-section products-section--alt">
          <div className="container">
            <div className="section-header">
              <h2>{t('home.customerFavourites')}</h2>
              <Link to="/shop?bestseller=true" className="section-header__link">{t('home.viewAll')} →</Link>
            </div>
            <div className="products-grid">
              {bestsellers.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      <style>{`
        /* HERO */
        .hero {
          min-height: 100vh; display: flex; align-items: center; position: relative; overflow: hidden;
          background: linear-gradient(135deg, #2d3b1a 0%, #4a5e2a 50%, #3d4a2a 100%);
          padding-top: 80px;
        }
        .hero__bg {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse at 70% 50%, rgba(212,148,58,0.15) 0%, transparent 60%);
        }
        .hero__content { position: relative; z-index: 2; max-width: 640px; }
        .hero__eyebrow {
          display: inline-block; padding: 6px 18px; background: rgba(212,148,58,0.2);
          color: var(--gold-light); border-radius: 50px; font-size: 0.82rem; font-weight: 600;
          letter-spacing: 1px; text-transform: uppercase; margin-bottom: 20px;
        }
        .hero__title {
          color: white; font-size: clamp(2.5rem, 6vw, 4rem); line-height: 1.1; margin-bottom: 20px;
        }
        .hero__title em { color: var(--gold-light); font-style: italic; }
        .hero__sub { color: rgba(255,255,255,0.75); font-size: 1.1rem; max-width: 480px; margin-bottom: 36px; }
        .hero__actions { display: flex; gap: 16px; margin-bottom: 40px; flex-wrap: wrap; }
        .hero__cta-secondary { color: white; border-color: rgba(255,255,255,0.4); }
        .hero__cta-secondary:hover { background: rgba(255,255,255,0.1); }
        .hero__trust { display: flex; gap: 24px; flex-wrap: wrap; }
        .hero__trust span { color: rgba(255,255,255,0.65); font-size: 0.82rem; }
        .hero__honeycomb {
          position: absolute; right: -60px; top: 50%; transform: translateY(-50%);
          display: grid; grid-template-columns: repeat(4, 80px); gap: 4px; opacity: 0.06;
        }
        [dir="rtl"] .hero__honeycomb { right: auto; left: -60px; }
        .honeycomb-cell {
          width: 80px; height: 90px; background: var(--gold);
          clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
        }

        /* CATEGORIES */
        .categories-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
        .category-card {
          background: var(--white); border-radius: var(--radius-lg); padding: 32px 20px;
          text-align: center; transition: all 0.3s; box-shadow: var(--shadow-sm);
        }
        .category-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-md); background: var(--olive); color: white; }
        .category-card__icon { font-size: 2.5rem; margin-bottom: 12px; }
        .category-card h3 { font-family: var(--font-serif); font-size: 1.1rem; margin-bottom: 6px; }
        .category-card span { font-size: 0.8rem; color: var(--text-muted); }
        .category-card:hover span { color: rgba(255,255,255,0.7); }

        /* SECTION HEADER */
        .section-header { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 36px; }
        .section-header h2 { }
        .section-header p { color: var(--text-muted); margin-top: 4px; }
        .section-header__link { color: var(--gold); font-weight: 600; font-size: 0.9rem; }

        /* PRODUCTS GRID */
        .products-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }
        .products-section--alt { background: var(--cream-dark); }

        /* WHY */
        .why-section { background: var(--olive-dark); }
        .why-section .section-header h2 { color: white; }
        .why-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }
        .why-card { text-align: center; color: white; padding: 32px 20px; }
        .why-card__icon { font-size: 2.5rem; display: block; margin-bottom: 16px; }
        .why-card h3 { font-family: var(--font-serif); font-size: 1.15rem; color: var(--gold-light); margin-bottom: 8px; }
        .why-card p { font-size: 0.88rem; opacity: 0.75; line-height: 1.7; }

        /* CTA BANNER */
        .cta-banner { background: var(--gold); padding: 60px 0; }
        .cta-banner__inner { display: flex; align-items: center; justify-content: space-between; gap: 32px; flex-wrap: wrap; }
        .cta-banner h2 { color: white; font-family: var(--font-serif); }
        .cta-banner p { color: rgba(255,255,255,0.85); margin-top: 6px; }
        .cta-banner .btn-primary { background: white; color: var(--gold); }
        .cta-banner .btn-primary:hover { background: var(--cream); }

        @media (max-width: 1024px) {
          .products-grid, .categories-grid, .why-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 600px) {
          .products-grid { grid-template-columns: repeat(2, 1fr); gap: 14px; }
          .categories-grid { grid-template-columns: repeat(2, 1fr); }
          .why-grid { grid-template-columns: 1fr 1fr; }
          .hero__honeycomb { display: none; }
          .cta-banner__inner { flex-direction: column; text-align: center; }
        }
      `}</style>
    </div>
  );
};

const getCatIcon = (slug) => {
  const icons = { 'raw-honey': '🍯', 'infused-honey': '✨', 'gift-sets': '🎁', 'honeycomb': '🐝' };
  return icons[slug] || '🍯';
};

export default HomePage;
